import { ActiveEventDetailOutputDto, FilterEventDto } from '@smatchy/sdk';

/**
 * The level filter and the order of the explore search, as the app holds and
 * sends them (Asana 1218854524230355, mockup validated by Maude on 22/10/2026).
 *
 * - three choices: every level, my level ±1 (the default), my level exactly;
 * - « Tous niveaux » activities are kept unless the person says otherwise;
 * - the recommended order (my sports, distance, level, date) unless the person
 *   asks for the nearest or the soonest first.
 *
 * WRITTEN FOR AN API THAT MAY BE OLDER THAN THE APP. A store release and an
 * API deployment never land together. The old API knows only
 * `includeOnlyMyLevel`, which meant the exact level and, for somebody with no
 * level, nothing but the « Tous niveaux » ones. So the default ±1 is sent
 * through the new field only: an old API ignores it and filters nothing,
 * rather than narrowing everybody to the exact level behind their back. The
 * exact level is also sent the old way, because that is what it always meant.
 */

export type LevelFilter = FilterEventDto.LevelFilterEnum;
export type SearchOrder = FilterEventDto.SearchOrderEnum;

export const DEFAULT_LEVEL_FILTER: LevelFilter = 'near';
export const DEFAULT_SEARCH_ORDER: SearchOrder = 'recommended';

const LEVEL_FILTERS: readonly string[] = Object.values(FilterEventDto.LevelFilterEnum);
const SEARCH_ORDERS: readonly string[] = Object.values(FilterEventDto.SearchOrderEnum);

/**
 * The filter a state holds. A state persisted before this field has only the
 * old toggle: on means the exact level, as it did; off is the new default.
 */
export function theLevelFilterOf(state: { levelFilter?: string; includeMyLevel?: boolean }): LevelFilter {
  if (state.levelFilter && LEVEL_FILTERS.includes(state.levelFilter)) {
    return state.levelFilter as LevelFilter;
  }
  return state.includeMyLevel ? 'exact' : DEFAULT_LEVEL_FILTER;
}

export function theSearchOrderOf(state: { searchOrder?: string }): SearchOrder {
  return state.searchOrder && SEARCH_ORDERS.includes(state.searchOrder)
    ? (state.searchOrder as SearchOrder)
    : DEFAULT_SEARCH_ORDER;
}

/** What the search request carries of the level and the order. */
export function theLevelAndOrderToSend(state: {
  levelFilter?: string;
  includeMyLevel?: boolean;
  keepAnyLevel?: boolean;
  searchOrder?: string;
}): Pick<FilterEventDto, 'levelFilter' | 'includeOnlyMyLevel' | 'keepAnyLevel' | 'searchOrder'> {
  const levelFilter = theLevelFilterOf(state);
  return {
    levelFilter,
    ...(levelFilter === 'exact' ? { includeOnlyMyLevel: true } : {}),
    // Absent means yes, for the API as for a state persisted before it.
    ...(state.keepAnyLevel === false ? { keepAnyLevel: false } : {}),
    searchOrder: theSearchOrderOf(state),
  };
}

/** Whether the level part of the filters differs from the default, for the count on the « Filtrer » chip. */
export function theLevelFilterIsChanged(state: {
  levelFilter?: string;
  includeMyLevel?: boolean;
  keepAnyLevel?: boolean;
}): boolean {
  return theLevelFilterOf(state) !== DEFAULT_LEVEL_FILTER || state.keepAnyLevel === false;
}

/**
 * The pill on an activity card. Only the two good news are said: « À ton
 * niveau » and « Proche de ton niveau ». An API older than `levelMatch` sends
 * nothing, and the card then shows what it showed before.
 */
export function theLevelPillOf(
  activity: Pick<ActiveEventDetailOutputDto, 'levelMatch'>,
): 'GENERICS.LEVEL_MATCH.EXACT' | 'GENERICS.LEVEL_MATCH.NEAR' | undefined {
  switch (activity.levelMatch) {
    case 'exact':
      return 'GENERICS.LEVEL_MATCH.EXACT';
    case 'near':
      return 'GENERICS.LEVEL_MATCH.NEAR';
    default:
      return undefined;
  }
}

/**
 * THE INVITATION TO GIVE ONE'S LEVEL. Somebody with no level in any sport
 * gets nothing from the level filter, so the results page invites them to
 * give one. « Plus tard » puts the invitation to rest for 30 days (Maude,
 * 22/10/2026): long enough not to nag, short enough to be seen again.
 */
export const LEVEL_INVITATION_REST_DAYS = 30;
export const LEVEL_INVITATION_STORAGE_KEY = 'levelInvitationPostponedAt';

export function theLevelInvitationIsShown(
  sportLevels: readonly { level?: number }[] | undefined,
  postponedAt: string | null | undefined,
  now: Date,
): boolean {
  if ((sportLevels ?? []).some((s) => typeof s.level === 'number')) {
    return false;
  }
  const postponed = postponedAt ? new Date(postponedAt).getTime() : NaN;
  if (Number.isNaN(postponed)) {
    return true;
  }
  return now.getTime() - postponed >= LEVEL_INVITATION_REST_DAYS * 24 * 60 * 60 * 1000;
}
